import React from 'react';

/** CSS-animated visual demo — no external media required */
export function HelpDemo({ type }) {
  const demos = {
    'clock-in': <ClockInDemo />,
    'schedule': <ScheduleDemo />,
    'shift-swap': <ShiftSwapDemo />,
    'timesheet': <TimesheetDemo />,
    'report': <ReportDemo />,
    'document': <DocumentDemo />,
    'staff': <StaffDemo />,
    'budget': <BudgetDemo />,
  };
  return demos[type] || null;
}

function ClockInDemo() {
  return (
    <div className="relative bg-slate-900 rounded-xl overflow-hidden p-6 my-4 flex items-center justify-center" style={{ minHeight: 180 }}>
      <div className="text-center">
        <div className="w-16 h-16 rounded-full bg-orange-500 flex items-center justify-center mx-auto mb-3 animate-pulse">
          <svg width="28" height="28" fill="none" stroke="white" strokeWidth="2.5" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
          </svg>
        </div>
        <p className="text-white font-semibold text-sm">Time Clock</p>
        <p className="text-slate-400 text-xs mt-1">Employee enters PIN → Clock In recorded</p>
        <div className="flex justify-center gap-2 mt-3">
          <span className="px-3 py-1 bg-green-500 text-white text-xs rounded-full font-medium">Clock In</span>
          <span className="px-3 py-1 bg-slate-600 text-slate-300 text-xs rounded-full font-medium">Clock Out</span>
        </div>
      </div>
    </div>
  );
}

function ScheduleDemo() {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
  const shifts = [
    { day: 0, label: '9am–5pm', color: 'bg-blue-500' },
    { day: 1, label: '2pm–10pm', color: 'bg-purple-500' },
    { day: 2, label: '9am–5pm', color: 'bg-blue-500' },
    { day: 3, label: 'OFF', color: 'bg-slate-600' },
    { day: 4, label: '12pm–8pm', color: 'bg-orange-500' },
  ];
  return (
    <div className="bg-slate-900 rounded-xl p-4 my-4">
      <p className="text-slate-400 text-xs mb-3">Weekly Schedule — John Smith</p>
      <div className="grid grid-cols-5 gap-2">
        {days.map((d, i) => (
          <div key={d} className="text-center">
            <p className="text-slate-500 text-xs mb-1">{d}</p>
            <div className={`rounded-lg py-2 px-1 text-white text-xs font-medium ${shifts[i].color}`} style={{ fontSize: 10 }}>
              {shifts[i].label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ShiftSwapDemo() {
  return (
    <div className="bg-slate-900 rounded-xl p-5 my-4">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold">A</div>
        <div>
          <p className="text-white text-xs font-medium">Alex → Maria</p>
          <p className="text-slate-400 text-xs">Requesting swap: Fri 2pm–10pm</p>
        </div>
        <span className="ml-auto px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full">Pending</span>
      </div>
      <div className="flex gap-2">
        <button className="flex-1 py-1.5 bg-green-500 text-white text-xs rounded-lg font-medium">Approve</button>
        <button className="flex-1 py-1.5 bg-red-500 text-white text-xs rounded-lg font-medium">Deny</button>
      </div>
    </div>
  );
}

function TimesheetDemo() {
  const rows = [
    { name: 'John S.', hours: '38.5h', status: 'Approved', color: 'bg-green-500' },
    { name: 'Maria R.', hours: '40h', status: 'Pending', color: 'bg-amber-500' },
    { name: 'Alex T.', hours: '22h', status: 'Rejected', color: 'bg-red-500' },
  ];
  return (
    <div className="bg-slate-900 rounded-xl p-4 my-4">
      <p className="text-slate-400 text-xs mb-3">Timesheet Review — Week of Mar 10</p>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.name} className="flex items-center justify-between bg-slate-800 rounded-lg px-3 py-2">
            <span className="text-white text-xs">{r.name}</span>
            <span className="text-slate-400 text-xs">{r.hours}</span>
            <span className={`px-2 py-0.5 ${r.color} text-white text-xs rounded-full`}>{r.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReportDemo() {
  const bars = [65, 80, 55, 90, 70, 85, 60];
  const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  return (
    <div className="bg-slate-900 rounded-xl p-5 my-4">
      <p className="text-slate-400 text-xs mb-4">Hours Worked — This Week</p>
      <div className="flex items-end gap-2 h-20">
        {bars.map((h, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <div className="w-full rounded-t bg-orange-500 transition-all" style={{ height: `${h}%` }} />
            <span className="text-slate-500 text-xs">{days[i]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DocumentDemo() {
  const docs = [
    { name: 'Employee Handbook.pdf', size: '2.1 MB', shared: true },
    { name: 'Safety Guidelines.pdf', size: '890 KB', shared: true },
    { name: 'Onboarding Form.docx', size: '340 KB', shared: false },
  ];
  return (
    <div className="bg-slate-900 rounded-xl p-4 my-4">
      <p className="text-slate-400 text-xs mb-3">Shared Documents</p>
      <div className="space-y-2">
        {docs.map((d) => (
          <div key={d.name} className="flex items-center gap-3 bg-slate-800 rounded-lg px-3 py-2">
            <div className="w-6 h-6 bg-red-500 rounded flex items-center justify-center flex-shrink-0">
              <svg width="10" height="12" fill="white" viewBox="0 0 10 12">
                <path d="M2 0h5l3 3v9H0V0h2z"/>
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-xs truncate">{d.name}</p>
              <p className="text-slate-500 text-xs">{d.size}</p>
            </div>
            {d.shared && <span className="text-xs text-green-400">Shared</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function StaffDemo() {
  return (
    <div className="bg-slate-900 rounded-xl p-5 my-4">
      <p className="text-slate-400 text-xs mb-3">Staff — Bella Italia Downtown</p>
      <div className="space-y-2">
        {[
          { name: 'John Smith', role: 'Staff', pin: '••••' },
          { name: 'Maria Rodriguez', role: 'Manager', pin: '••••' },
          { name: 'Alex Thompson', role: 'Staff', pin: '••••' },
        ].map((s) => (
          <div key={s.name} className="flex items-center gap-3 bg-slate-800 rounded-lg px-3 py-2">
            <div className="w-7 h-7 rounded-full bg-orange-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
              {s.name[0]}
            </div>
            <div className="flex-1">
              <p className="text-white text-xs font-medium">{s.name}</p>
            </div>
            <span className="text-slate-400 text-xs">{s.role}</span>
            <span className="text-slate-500 text-xs">PIN: {s.pin}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BudgetDemo() {
  return (
    <div className="bg-slate-900 rounded-xl p-5 my-4">
      <p className="text-slate-400 text-xs mb-4">Budget vs Actual — This Week</p>
      <div className="space-y-3">
        {[
          { loc: 'Downtown', budget: 5000, actual: 3800, pct: 76 },
          { loc: 'Uptown', budget: 4000, actual: 4200, pct: 105, over: true },
        ].map((l) => (
          <div key={l.loc}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-white">{l.loc}</span>
              <span className={l.over ? 'text-red-400' : 'text-green-400'}>
                ${l.actual.toLocaleString()} / ${l.budget.toLocaleString()}
              </span>
            </div>
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${l.over ? 'bg-red-500' : 'bg-green-500'}`}
                style={{ width: `${Math.min(l.pct, 100)}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
